import { useEffect, useState } from "react";

/**
 * `useDevicePixelRatio` — the live display device-pixel-ratio, capped at `maxDpr`,
 * that re-renders the consumer when the ratio changes.
 *
 * WHY. A `<canvas>` is crisp only when its BACKING STORE is sized `cssPx · dpr`
 * (web-page-layout `render.ts` — `canvas.width = round(cssW · min(dpr, maxDpr))`).
 * The paginated editor reads `globalThis.devicePixelRatio` ONCE when it mounts, so
 * a window dragged from a standard display onto a hi-dpi (Retina) one — or a
 * browser-zoom change, which also moves `devicePixelRatio` — leaves the page
 * rastered at the OLD ratio (soft text) until a remount. Keying the canvas mount
 * on this hook re-scales the backing store the moment the ratio changes.
 *
 * The cap (default 2) matches the engine's `maxDpr` default: beyond 2× the
 * anti-aliasing gain is imperceptible and the backing store just wastes memory.
 *
 * A CSS `resolution` media query matches a SINGLE ratio and fires once when the
 * viewport crosses it, so we re-arm the listener at the new ratio after every
 * change to keep tracking. No-op (returns the mount-time value) where
 * `matchMedia` is absent (Node/Bun unit env).
 */
export function useDevicePixelRatio(maxDpr = 2): number {
  const [dpr, setDpr] = useState(() => cappedDpr(maxDpr));
  useEffect(() => {
    if (typeof globalThis.matchMedia !== "function") return;
    let mql: MediaQueryList | null = null;
    const onChange = (): void => {
      setDpr(cappedDpr(maxDpr));
      arm(); // re-arm at the NEW ratio (a resolution query fires only once)
    };
    const arm = (): void => {
      mql?.removeEventListener("change", onChange);
      const raw = globalThis.devicePixelRatio || 1;
      mql = globalThis.matchMedia(`(resolution: ${raw}dppx)`);
      mql.addEventListener("change", onChange);
    };
    arm();
    // Reconcile once in case the ratio moved between the initial render and the
    // effect running (e.g. the mount raced an OS display switch).
    setDpr(cappedDpr(maxDpr));
    return () => mql?.removeEventListener("change", onChange);
  }, [maxDpr]);
  return dpr;
}

/** The current `globalThis.devicePixelRatio` (default 1 when absent) clamped to
 *  `[1, maxDpr]`. Pure — the unit-testable core of {@link useDevicePixelRatio}. */
export function cappedDpr(maxDpr = 2): number {
  const raw = globalThis.devicePixelRatio || 1;
  return Math.min(Math.max(raw, 1), maxDpr);
}
